import * as yup from 'yup';
import { useAisQuery } from '@gov-nx/core/hooks';
import type { FormDefinition2 } from '@gov-nx/core/service';
import { useFormBuilderWithConsent, useLocale } from '@gov-nx/core/service';
import type { Nullable } from '@gov-nx/core/types';
import type {
	CommonMultiselectProps,
	FormAutocompleteProps,
	FormInputProps,
} from '@gov-nx/ui/types';
import {
	getMultiselectDefinition,
	resetHours,
	today,
	useDateDefinition,
	yearsAgo,
} from '@gov-nx/utils/common';
import type { ServiceCode } from '../definitions/codes';

export interface useFormProps {
	code: ServiceCode;
	isDownloading: boolean;
	isLowAuthorization: boolean;
}

export interface FormFields {
	datumOd: FormInputProps;
	datumDo: FormInputProps;
	ais: CommonMultiselectProps;
	ais1: FormAutocompleteProps;
}

export interface FilterForm {
	datumOd: Nullable<Date>;
	datumDo: Nullable<Date>;
	ais: Nullable<number[]>;
}

const DEFAULT_DATUM_OD_DIFF_YEARS = 2;

export function useForm({
	code,
	isDownloading,
	isLowAuthorization,
}: useFormProps): FormDefinition2<FilterForm, FormFields> {
	const { t } = useLocale(code);

	const { data: aisData, isFetching } = useAisQuery();

	const defaultDatumOd = yearsAgo(DEFAULT_DATUM_OD_DIFF_YEARS);

	return useFormBuilderWithConsent<FilterForm, FormFields>(
		[
			useDateDefinition({
				name: 'datumOd',
				isRequired: true,
				label: t('formular.datumOd.label'),
				defaultValue: defaultDatumOd,
				min: {
					min: resetHours(defaultDatumOd),
					errorMessage: t('formular.datumOd.validation.min'),
				},
				isDisabled: isDownloading,
			}),
			useDateDefinition({
				name: 'datumDo',
				isRequired: true,
				label: t('formular.datumDo.label'),
				defaultValue: today(),
				min: {
					min: yup.ref('datumOd'),
					errorMessage: t('formular.datumDo.validation.starsi-nez-datum-od'),
				},
				isDisabled: isDownloading,
			}),
			getMultiselectDefinition({
				name: 'ais',
				label: t('formular.ais.label'),
				options:
					aisData?.map((d) => ({
						label: d.nazev ?? '',
						value: d.id?.toString() ?? d.kod ?? '',
					})) ?? [],
				placeholder: t('formular.ais.placeholder'),
				message: t('formular.ais.message'),
				isDisabled: isDownloading || isFetching,
			}),
		],
		{
			isDisabled: isDownloading,
			isLowAuthorization,
		}
	);
}
